import { countConditions } from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";
import type {
  CatalogRepository,
  CollectionRef,
  Guarded,
  HsnCode,
  ListQuery,
  Page,
  Product,
  ProductWithVariants,
  Variant,
  WriteResult,
} from "@estelle/domain";

/**
 * Catalog, over HTTP.
 *
 * Nothing is mapped on arrival. The API returns the shapes `packages/domain`
 * defines, because both sides import the same package — a field renamed there
 * fails to compile on both, rather than arriving as `undefined` at runtime in
 * one of them.
 *
 * `updateProduct` and `setVariantHsn` are Shopify mutations (FR-CAT-6,
 * FR-CAT-7) and go through `apps/api`'s write routes, which send them through
 * `writeThrough()` to Shopify (#170). What comes back on success is the
 * product or variant as Shopify returned it; a refusal is a `WriteResult`
 * rejection to show, not an error to throw — same contract `adapters/api/
 * orders.ts`'s `write()` follows.
 */

async function get<T>(path: string, params: URLSearchParams): Promise<T> {
  const query = params.toString();
  const response = await apiFetch(
    `/api/catalog${path}${query ? `?${query}` : ""}`,
  );

  if (!response.ok) {
    /*
      The status and the path, not the body. A failed response can carry
      request context, and this message reaches an error state a user reads —
      "Not found" is useful, an echoed payload is not.
    */
    throw new Error(
      `Catalog request failed: ${String(response.status)} on ${path}`,
    );
  }

  return (await response.json()) as T;
}

function listParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  /*
    The filter tree travels as the domain's own JSON, not the URL's short
    wire form: the API imports the same `Filter` type, so there is no second
    encoding to keep in step. Filtering and sorting happen in Postgres, over
    every product rather than the page on screen.
  */
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

const fromShopify = {
  ok: false,
  rejection: {
    kind: "blocked",
    message:
      "HSN codes come from Shopify. Set the code on the variant there and it joins the master on the next sync.",
  },
} as const;

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx or
 * 503 status — a rejection to show. Anything else is a transport failure and
 * throws.
 */
async function write<T>(path: string, body: unknown): Promise<WriteResult<T>> {
  const response = await apiFetch(`/api/catalog${path}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Catalog write failed: ${String(response.status)} on ${path}`,
  );
}

export function createApiCatalog(): CatalogRepository {
  return {
    listProducts: (query: ListQuery): Promise<Page<Product>> =>
      get<Page<Product>>("/products", listParams(query)),

    getProduct: async (id): Promise<ProductWithVariants | null> => {
      const response = await apiFetch(`/api/catalog/products/${id}`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(`Catalog request failed: ${String(response.status)}`);
      }
      return (await response.json()) as ProductWithVariants;
    },

    listVariantsMissingHsn: (query: ListQuery): Promise<Page<Variant>> =>
      get<Page<Variant>>("/variants/missing-hsn", listParams(query)),

    listCollections: (query: ListQuery): Promise<Page<CollectionRef>> =>
      get<Page<CollectionRef>>("/collections", listParams(query)),

    listHsnCodes: (query: ListQuery): Promise<Page<HsnCode>> =>
      get<Page<HsnCode>>("/hsn-codes", listParams(query)),

    listProductsByHsn: (code, query): Promise<Page<Product>> =>
      get<Page<Product>>(
        `/hsn-codes/${encodeURIComponent(code)}/products`,
        listParams(query),
      ),

    updateHsnCode: async (id, patch): Promise<WriteResult<HsnCode>> => {
      const response = await apiFetch(`/api/catalog/hsn-codes/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      // A refusal arrives as a `WriteResult` body with a 4xx status — that is
      // a rejection to show, not a failure to throw.
      const body = (await response
        .json()
        .catch(() => null)) as WriteResult<HsnCode> | null;
      if (body !== null && typeof body === "object" && "ok" in body) {
        return body;
      }
      throw new Error(`HSN update failed: ${String(response.status)}`);
    },

    /*
      Codes are not created or deleted here. A code is the variant's, set in
      Shopify, and the sync adds it to the master; a master entry created by
      hand would match no product, and deleting one in use would leave its
      products with no rate to invoice at (FR-GST-8).
    */
    createHsnCode: () => Promise.resolve(fromShopify),
    deleteHsnCode: () => Promise.resolve(fromShopify),

    updateProduct: (id, patch, guard: Guarded): Promise<WriteResult<Product>> =>
      write<Product>(`/products/${encodeURIComponent(id)}`, {
        ...patch,
        ...guard,
      }),

    setVariantHsn: (
      id,
      hsnCode,
      guard: Guarded,
    ): Promise<WriteResult<Variant>> =>
      write<Variant>(`/variants/${encodeURIComponent(id)}/hsn`, {
        hsnCode,
        ...guard,
      }),
  };
}
